import { useCallback, useSyncExternalStore } from "react";
import { STORYDOC_VARIABLES } from "../../data/storydoc-variables";
import type { PickerTab, PickerVariable } from "../types";

const STORAGE_KEY = "sd-variable-picker-recents";
const MAX_RECENTS = 5;

type RecentsByTab = Partial<Record<PickerTab, PickerVariable[]>>;

const read = (): RecentsByTab => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as RecentsByTab) : {};
  } catch {
    return {};
  }
};

const write = (recents: RecentsByTab): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recents));
  } catch {
    // best-effort — ignore quota / private-mode errors
  }
};

// One module-level store (not per-component state): a variable used through
// drag-and-drop is recorded by the drop handler, and every mounted picker must
// see it at once — with component state each picker only knew its own clicks.
let recents: RecentsByTab | null = null;
const listeners = new Set<() => void>();

const getSnapshot = (): RecentsByTab => {
  if (recents === null) recents = read();
  return recents;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const STORYDOC_KEYS = new Set(STORYDOC_VARIABLES.map((v) => v.key));

/** Which picker tab a variable belongs to (for recording it as recent). */
export const tabForVariable = (variable: PickerVariable): PickerTab => {
  const bare = variable.token
    .replace(/^\{\{\{?\s*/, "")
    .replace(/\s*\}?\}\}$/, "");
  if (bare.startsWith("hubspot.")) return "hubspot";
  if (bare.startsWith("fathom.")) return "fathom";
  if (STORYDOC_KEYS.has(bare) || STORYDOC_KEYS.has(variable.id))
    return "storydoc";
  return "custom";
};

/**
 * Records a use of `variable` (most-recent first, de-duplicated by id, five
 * per tab) and notifies every mounted picker. Callable from non-React code —
 * the drag-and-drop drop handler uses it.
 */
export const recordRecentVariable = (
  variable: PickerVariable,
  tab: PickerTab = tabForVariable(variable),
): void => {
  const current = getSnapshot();
  const existing = current[tab] ?? [];
  const deduped = [
    variable,
    ...existing.filter((v) => v.id !== variable.id),
  ].slice(0, MAX_RECENTS);
  recents = { ...current, [tab]: deduped };
  write(recents);
  listeners.forEach((listener) => listener());
};

/** Test-only: forget everything (memory + storage). */
export const resetRecentVariables = (): void => {
  recents = {};
  write(recents);
  listeners.forEach((listener) => listener());
};

/**
 * Per-tab "recently used" variables, persisted to localStorage and shared by
 * every picker instance. Keeps the last five used variables per tab,
 * most-recent first, de-duplicated by id.
 */
export const useRecentVariables = () => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const getRecents = useCallback(
    (tab: PickerTab): PickerVariable[] => snapshot[tab] ?? [],
    [snapshot],
  );

  const addRecent = useCallback((tab: PickerTab, variable: PickerVariable) => {
    recordRecentVariable(variable, tab);
  }, []);

  return { getRecents, addRecent };
};
